import { AsyncPipe, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { filter, take } from "rxjs/operators";
import { DialogFrameModule } from "src/app/common/components/dialog-frame/dialog-frame.module";
import { CheckboxFieldModule } from "src/app/common/components/form/checkbox-field/checkbox-field.module";
import { InputFieldModule } from "src/app/common/components/form/input-field/input-field.module";
import { NoticeComponent } from "src/app/common/components/notice/notice.component";
import { CreateRegionRequestDto } from "src/app/common/dto/region/create-region-request";
import { decimalRangeValidator } from "src/app/common/validators/decimal-range.validator";
import { EneButtonModule } from "src/app/shared/ui/button/legacy/button.module";
import { SelectFieldComponent } from "src/app/shared/ui/form-fields/select-field/select-field.component";
import {
  CreateMunicipalityDialogComponentStore,
  CreateMunicipalityStep
} from "./create-municipality-dialog.component-store";
import {
  AGS_LENGTH,
  AGS_PATTERN,
  DEFAULT_ZOOM_LEVEL,
  LATITUDE_RULES,
  LONGITUDE_RULES,
  MUNICIPALITY_NAME_MAX_LENGTH,
  MUNICIPALITY_NAME_PATTERN,
  ZOOM_LEVEL_OPTIONS
} from "./create-municipality-dialog.constants";

/** The lookup returns raw floats (e.g. `9.180013234567891`) that would fail the decimal limit. */
const toCoordinateValue = (value: number, maxDecimals: number): string => `${Number(value.toFixed(maxDecimals))}`;

interface AgsForm {
  ags: FormControl<string>;
}

interface MunicipalityDetailsForm {
  regionId: FormControl<string>;
  ags: FormControl<string>;
  name: FormControl<string>;
  longitude: FormControl<string>;
  latitude: FormControl<string>;
  zoomLevel: FormControl<string>;
  hasElectricityDivision: FormControl<boolean>;
  hasGasDivision: FormControl<boolean>;
}

@Component({
  selector: "app-create-municipality-dialog",
  templateUrl: "./create-municipality-dialog.component.html",
  styleUrls: ["./create-municipality-dialog.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    AsyncPipe,
    ReactiveFormsModule,
    TranslateModule,
    DialogFrameModule,
    InputFieldModule,
    CheckboxFieldModule,
    SelectFieldComponent,
    NoticeComponent,
    EneButtonModule
  ],
  providers: [CreateMunicipalityDialogComponentStore]
})
export class CreateMunicipalityDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly componentStore = inject(CreateMunicipalityDialogComponentStore);

  readonly agsLength = AGS_LENGTH;
  readonly zoomLevelOptions = ZOOM_LEVEL_OPTIONS;
  readonly vm$ = this.componentStore.vm$;

  readonly agsForm: FormGroup<AgsForm> = this.fb.nonNullable.group({
    ags: ["", [Validators.required, Validators.pattern(AGS_PATTERN)]]
  });

  readonly detailsForm: FormGroup<MunicipalityDetailsForm> = this.fb.nonNullable.group({
    regionId: [{ value: "", disabled: true }],
    ags: [{ value: "", disabled: true }],
    name: [
      "",
      [
        Validators.required,
        Validators.pattern(MUNICIPALITY_NAME_PATTERN),
        Validators.maxLength(MUNICIPALITY_NAME_MAX_LENGTH)
      ]
    ],
    longitude: ["", [Validators.required, decimalRangeValidator(LONGITUDE_RULES)]],
    latitude: ["", [Validators.required, decimalRangeValidator(LATITUDE_RULES)]],
    zoomLevel: [DEFAULT_ZOOM_LEVEL, Validators.required],
    hasElectricityDivision: [false],
    hasGasDivision: [false]
  });

  constructor() {
    this.agsForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.componentStore.clearAgsLookupError());

    this.componentStore.agsLookup$.pipe(filter(Boolean), take(1), takeUntilDestroyed()).subscribe((lookup) => {
      const { name, longitude, latitude } = this.detailsForm.controls;

      this.detailsForm.patchValue({
        regionId: lookup.regionId,
        ags: lookup.ags,
        name: lookup.name,
        longitude: toCoordinateValue(lookup.longitude, LONGITUDE_RULES.maxDecimals),
        latitude: toCoordinateValue(lookup.latitude, LATITUDE_RULES.maxDecimals)
      });
      // Prefilled values are not typed by the user; mark them so an invalid lookup value shows its message.
      [name, longitude, latitude].forEach((control) => control.markAsDirty());
    });
  }

  onSubmit(step: CreateMunicipalityStep): void {
    if (step === "ags") {
      this.submitAgs();
    } else {
      this.submitDetails();
    }
  }

  private submitAgs(): void {
    if (this.agsForm.invalid) {
      return;
    }

    this.componentStore.lookupAgs(this.agsForm.getRawValue().ags.trim());
  }

  private submitDetails(): void {
    if (this.detailsForm.invalid) {
      return;
    }

    this.componentStore.createMunicipality(this.toCreateRegionRequest());
  }

  private toCreateRegionRequest(): CreateRegionRequestDto {
    const value = this.detailsForm.getRawValue();

    return {
      region: {
        ags: value.ags,
        regionId: value.regionId,
        name: value.name.trim(),
        longitude: Number(value.longitude.trim().replace(",", ".")),
        latitude: Number(value.latitude.trim().replace(",", ".")),
        zoomLevel: Number(value.zoomLevel),
        hasElectricityDivision: value.hasElectricityDivision,
        hasGasDivision: value.hasGasDivision
      }
    };
  }
}
